
import { defineComponent } from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import Card from "primevue/card";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";
import Footer from "@/components/Footer.vue";
import PaymentCard from "@/components/PaymentCard.vue";
import Tooltip from "primevue/tooltip";
import NotificationCard from "@/components/Notification-Card.vue";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from 'primevue/column';
import Badge from 'primevue/badge';
import { FilterMatchMode } from "primevue/api";
import Utils from '@/utility/utils';
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {
  useInvoiceColumnVisibility,
  InvoiceColumn,
} from "@/composables/useInvoiceColumnVisibility";
export default defineComponent({
  name: "Invoice",
  components: {
    Card,
    Calendar,
    Dropdown,
    Footer,
    PaymentCard,
    NotificationCard,
    InputText,
    Button,
    DataTable,
    Column,
    Badge,
    LoadingSpinner
  },
  directives: {
    tooltip: Tooltip,
  },
  setup() {
    const {
      visibleColumns,
      visiblePrintableColumns,
      applyVisibility,
      resetVisibility,
    } = useInvoiceColumnVisibility();
    return {
      visibleColumns,
      visiblePrintableColumns,
      applyVisibility,
      resetVisibility,
    };
  },
  data() {
    return {
      totalRecords: 0,
      maxExportRows: 1000,
      prefetchBatchSize: 100,
      isLoadingTickets: true,
      appendFetchInFlight: false,
      appendFetchRequestKey: "",
      isArFdictReady: false,
      arFdictLoadFailed: false,
      primaryColor: "#ff6600",
      first: 0,
      rows: 50,
      dueDateFilter: null,
      selectedType: null,
      selectedStatus: "O",
      invoiceNumber: "",
      dateSortActive: true,
      balSortActive: false,
      displayPayment: false,
      windowSize: window.innerHeight,
      stati: [
        { name: "Outstanding", code: "O" },
        { name: "Paid", code: "P" },
        { name: "Payment Pending", code: "Y" },
      ],
      types: [
        { name: "Credit Memo", code: "CM" },
        { name: "Debit Memo", code: "DM" },
        { name: "Invoice", code: "IN" },
        { name: "On Account", code: "OA" },
      ],
      sort: {
        sortBy: "sortableDate",
        sortOrder: -1,
      },
      statCards: [
        {
          title: "Outstanding Invoices Count 1",
          number: 25,
        },
        {
          title: "Outstanding Invoices Count 2 ",
          number: 25,
        },
        {
          title: "Outstanding Invoices Count 3",
          number: 25,
        },
      ],
      columnStyle: "col-12 md:col-1 md:flex-grow-1 lg:col-1 lg:flex-grow-1",
      hover: {},
      filters: {
        status: { value: [], matchMode: FilterMatchMode.IN, },
        type: { value: [], matchMode: FilterMatchMode.IN, },
        ar_id: { value: "", matchMode: FilterMatchMode.EQUALS, },
        sortableDate: { value: [], matchMode: FilterMatchMode.BETWEEN, }
      },
    };
  },
  computed: {
    ...mapState(["invoice", "customer", "payment", "session"]),
    ...mapGetters({
      getRange: "invoice/getRange",
      getPage: "invoice/getPage",
      getLength: "invoice/getLength",
      getInvoices: "invoice/getInvoices",
      getCust: "customer/getCust",
      getFirstCust: "session/getFirstCust",
      getInvoice: "invoice/getInvoice",
      getLoadingInvoices: "invoice/getLoadingInvoices",
      getLoadingStatus: "invoice/getLoadingStatus",
      getAR: "control/getAR",
    }),
    resizeRight() {
      if (this.windowSize < 769) {
        return "text-center";
      }
      return "text-right";
    },
    resizeLeft() {
      if (this.windowSize < 769) {
        return "text-center";
      }
      return "text-left";
    },
    typesForSearch(): string[] {
      const codes = this.types.map(type => type.code);
      return codes;
    },
    pagedInvoices(): Array<any> {
      if (!Array.isArray(this.getInvoices)) {
        return [];
      }
      return this.getInvoices.slice(this.first, this.first + this.rows);
    },
  },
  async created() {
    const control = {
        id: "AR1", // ToDo: When multi-company is setup, this will need to be dynamic
        procedure: "AR.CONTROL",
        filename: "CONTROL",
        fieldnames:"ar_type_name|ar_type_code|visible|ar_disable_invoice_link",
      };
    await this.fetchControls(control);
    try {
      await this.fetchFdict("AR");
      this.isArFdictReady = true;
    } catch (error) {
      this.arFdictLoadFailed = true;
    }
    this.applyColumnVisibility();
    this.setVisibleARTypes();
    await this.fetchFilteredInvoices(false);
  },

  methods: {
    ...mapActions({
      fetchInvoice: "invoice/fetchInvoice",
      fetchControls: "control/fetchControls",
      fetchFdict: "fdict/fetchFdict",
      setInvoice: "invoice/setInvoices",
      setNextPage: "invoice/setNextPage",
      addInvoiceToPay: "invoice/addInvoiceToPay",
      removeInvoiceToPay: "invoice/removeInvoiceToPay",
      addNotification: "notification/add",
      clearPrintData: "printableDatatable/clearData",
      setPrintData: "printableDatatable/setData",
      setPrintDefaultColumns: "printableDatatable/setDefaultColumns",
      setPrintAvaialbleColumns: "printableDatatable/setAvaialbleColumns",
      clearInvoicesToPay: "invoice/clearInvoicesToPay",
      setInvoices: "invoice/setInvoices",
    }),
    async printTable() {
      //block a print with no criteria applied
      if(this.totalRecords === 0) {
        const notification = {
          message: `No records to print or export.`,
          type: "error",
        };
        this.addNotification(notification);
        return;
      }
      if (this.totalRecords > this.maxExportRows) {
        const notification = {
          message: `Please filter the results before printing.`,
          type: "error",
        };
        this.addNotification(notification);
      } else {
        const previousFirst = this.first;
        const previousRows = this.rows;
        this.clearPrintData();
        // get full dataset for the current criteria
        await this.fetchFilteredInvoices(false, {
          rangeStart: 1,
          rangeEnd: this.maxExportRows,
          preservePagination: true,
        });
        this.setPrintData(this.getInvoices.map((item: any) => {
              return {
                ar_id: item.ar_id,
                cust_name: item.cust_name,
                invoice_date: item.invoice_date,
                due_date: item.due_date,
                balance: item.balance,
                type: this.setTypeFilter(item.type),
                status: this.setStatusFilter(item.status),
                invoice_amt: item.invoice_amt,
              };
            }));
        this.setPrintDefaultColumns(this.visiblePrintableColumns);
        this.setPrintAvaialbleColumns(this.visiblePrintableColumns);

        window.open(
          "/printable-view?print=1&showSelection=1",
          "_blank"
        );
        this.rows = previousRows;
        this.first = previousFirst;
        await this.fetchFilteredInvoices(false, {
          rangeStart: 1,
          rangeEnd: this.prefetchBatchSize,
          preservePagination: true,
        });
        while (
          this.first + this.rows > this.getInvoices.length &&
          this.getInvoices.length < this.totalRecords
        ) {
          await this.fetchFilteredInvoices(true, {
            rangeStart: this.getInvoices.length + 1,
            rangeEnd: this.getInvoices.length + this.prefetchBatchSize,
            preservePagination: true,
          });
        }
      }
    },
    setStatusFilter(status: string) {
      switch(status) {
        case "O":
          return "Outstanding";
        case "P":
          return "Paid";
        case "Y":
          return "Payment Pending";
        default:
          return "NA";
      }
    },
    setTypeFilter(type: string) {
      switch(type) {
        case "CM":
          return "Credit Memo";
        case "DM":
          return "Debit Memo";
        case "IN":
          return "Invoice";
        case "OA":
          return "On Account";
        default:
          return "NA";
      }
    },
    setVisibleARTypes() {
      const arTypes = this.getAR?.ar_type_name_items;
      if (Array.isArray(arTypes) && arTypes.length > 0) {
        this.types = this.types.filter((type) => {
        const arEntry = arTypes.find((ar: { ar_type_code: string; }) => ar.ar_type_code === type.code);
        return arEntry !== undefined && arEntry.visible === 'Y';
        });
      }
    },
    isInvoiceLinkDisabled(): boolean {
      return this.getAR?.ar_disable_invoice_link === "Y";
    },
    applyColumnVisibility() {
      if (this.arFdictLoadFailed || !this.isArFdictReady) {
        this.resetVisibility();
      } else {
        this.applyVisibility();
      }
    },
    formatBodyValue(col: InvoiceColumn, data: any): string {
      switch (col.bodyType) {
        case "date":
          return this.formatDate(data[col.dataField || col.field]);
        case "currency":
          return this.formatCurrency(data[col.field]);
        case "type":
          return this.types.find((t: any) => t.code === data[col.field])?.name || "";
        case "text":
        default:
          return data[col.field];
      }
    },
    clearColumnFilter(col: InvoiceColumn) {
      if (col.filterStateKey) {
        (this as any)[col.filterStateKey] = col.filterType === "text" ? "" : null;
      }
      this.fetchFilteredInvoices(false);
    },
    getFilterValue(col: InvoiceColumn): any {
      return (this as any)[col.filterStateKey!];
    },
    setFilterValue(col: InvoiceColumn, value: any) {
      (this as any)[col.filterStateKey!] = value;
    },
    getFilterOptions(col: InvoiceColumn): any[] {
      return (this as any)[col.filterOptionsKey!];
    },
    sortInvoices(event: any) {
      this.sort.sortOrder = event.sortOrder
      this.sort.sortBy = event.sortField
      this.first = 0;
      this.fetchFilteredInvoices(false)
    },
    customerId(): string {
      if (this.getCust !== null) return this.getCust.cust_id;
      else {
        return this.getFirstCust.cust_id;
      }
    },
    formatCurrency(value: string) {
      const amount = parseFloat(value);

      if(isNaN(amount)) {
        return 'error';
      } else {
        return amount.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
        })
      }
    },
    formatDate(date: string) {
      return Utils.formatDate(date);
    },
    fetchInvoiceRecord(recordId: string) {
      this.fetchInvoice({
        recordId: recordId,
      });
    },

    async fetchFilteredInvoices(
      addInvoices: boolean,
      options?: { rangeStart?: number; rangeEnd?: number; preservePagination?: boolean }
    ) {
      const shouldResetPage = !addInvoices && !options?.preservePagination;
      const rangeStart = options?.rangeStart
        ?? (addInvoices ? this.getInvoices.length + 1 : 1);
      const rangeEnd = options?.rangeEnd
        ?? (addInvoices
          ? this.getInvoices.length + this.prefetchBatchSize
          : this.prefetchBatchSize);

      let sortBy
      switch(this.sort.sortBy) {
        case "invoice_amt":
          sortBy = "invoice.amt";
          break;
        case "invoice_date":
          sortBy = "invoice.date";
          break;
        case "sortableDate":
          sortBy = "due.date";
          break;
        case "cust_name":
          sortBy = "cust";
          break;
        default:
          sortBy = this.sort.sortBy
          break;
      }
      if(this.selectedStatus) {
        this.filters.status.value = [this.selectedStatus]  as any
      } else {
        this.filters.status.value = null as any
      }
      if(this.selectedType) {
        this.filters.type.value = [this.selectedType] as any
      }
      else {
        this.filters.type.value = []
      }
      this.filters.sortableDate.value = this.dueDateFilter || []
      this.filters.ar_id.value = this.invoiceNumber

      const appendRequestKey = `${rangeStart}-${rangeEnd}-${sortBy}-${this.sort.sortOrder}-${this.selectedStatus ?? ""}-${this.selectedType ?? ""}-${this.invoiceNumber ?? ""}-${JSON.stringify(this.dueDateFilter || [])}`;
      if (addInvoices) {
        if (this.appendFetchInFlight && this.appendFetchRequestKey === appendRequestKey) {
          return;
        }
        this.appendFetchInFlight = true;
        this.appendFetchRequestKey = appendRequestKey;
      }

      this.isLoadingTickets = true;
      try {
        const resp = await this.setInvoice({
          custId: this.customerId(),
          dateRange: this.dueDateFilter,
          status: this.selectedStatus,
          addInvoices: addInvoices,
          id: this.invoiceNumber,
          type: this.selectedType !== null ? this.selectedType : this.typesForSearch,
          sortOrder: this.sort.sortOrder === -1 ? "DEC" : "ASC",
          sortBy: sortBy,
          rangeStart,
          rangeEnd,
        })

        if (typeof resp.totalRecords === "number" && resp.totalRecords >= 0) {
          this.totalRecords = resp.totalRecords;
        } else {
          this.totalRecords = this.getInvoices.length;
        }
        this.applyColumnVisibility();
      } finally {
        this.isLoadingTickets = false;
        if (addInvoices && this.appendFetchRequestKey === appendRequestKey) {
          this.appendFetchInFlight = false;
          this.appendFetchRequestKey = "";
        }
      }

      if (shouldResetPage) {
        this.first = 0;
      }
    },
    forceUpdate() {
      this.$forceUpdate();
    },
    onPage(event: any) {
      this.setNextPage(event.first);
      this.first = event.first;
      this.rows = event.rows;
      const requestedRangeEnd = event.first + event.rows;
      if (
        requestedRangeEnd > this.getInvoices.length &&
        this.getInvoices.length < this.totalRecords
      ) {
        this.fetchFilteredInvoices(true, {
          rangeStart: this.getInvoices.length + 1,
          rangeEnd: this.getInvoices.length + this.prefetchBatchSize,
          preservePagination: true,
        });
      }
    },
    onRowsPerPageChange(rows: number) {
      if (rows === this.rows) {
        return;
      }
      this.rows = rows;
      this.first = 0;
      if (
        rows > this.getInvoices.length &&
        this.getInvoices.length < this.totalRecords
      ) {
        this.fetchFilteredInvoices(true, {
          rangeStart: this.getInvoices.length + 1,
          rangeEnd: this.getInvoices.length + this.prefetchBatchSize,
          preservePagination: true,
        });
      }
    },
    resize(event: any) {
      this.windowSize = window.innerWidth;
    },
    invoiceIcon(id: string) {
      let downloaded = this.getInvoice;
      let downloading = this.getLoadingInvoices.includes(id);
      return {
        "pi pi-download":
          downloaded.find((i: any) => i.invoice_num === id) === undefined &&
          !downloading,
        "pi pi-spin pi-spinner": downloading,
        "pi pi-file-pdf":
          downloaded.find((i: any) => i.invoice_num === id) !== undefined &&
          !downloading,
      };
    },
    inPayArray(id: string) {
      const found = this.invoice.invoicesToPay.find((inv: any) => {
        return inv.arId == id;
      });
      if (found) return true;

      return false;
    },
    addInvoice(data: any) {
      this.addInvoiceToPay({
        arId: data.ar_id,
        balance: parseFloat(data.balance),
        dueDate: data.due_date,
      });
    },
    removeInvoice(data: any) {
      this.removeInvoiceToPay({
        arId: data.ar_id,
        balance: parseFloat(data.balance),
      });
    },
    onPaymentCompleted() {
      this.fetchFilteredInvoices(false);
    },
  },
  watch: {
    getCust() {
      this.fetchFilteredInvoices(false);
      this.clearInvoicesToPay();

    }
  },
  mounted() {
    window.addEventListener("resize", this.resize);
  },
});
